import { useLocation, useNavigate } from 'react-router-dom';
import { LanguageCode, getLocalizedPath, isEnglishPath } from '@/lib/index';

/**
 * Custom hook for managing bilingual state (Japanese/English) based on URL routing.
 * Handles language detection from path prefixes, manual language switching,
 * and persistence of language preference via localStorage.
 */
export const useLanguage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine the current active language based on the URL path prefix (/en)
  const currentLanguage: LanguageCode = isEnglishPath(location.pathname) ? 'en' : 'jp';

  /**
   * Changes the current language by navigating to the localized route
   * and persisting the choice to localStorage.
   */
  const changeLanguage = (lang: LanguageCode) => {
    if (lang === currentLanguage) return;

    // Persist preference for future visits
    localStorage.setItem('mmk_preferred_language', lang);

    // Generate the localized path for the current page
    const targetPath = getLocalizedPath(location.pathname, lang);
    
    // Navigate to the new localized URL
    navigate(targetPath);
  };

  /**
   * Convenience function to toggle between Japanese and English.
   */
  const toggleLanguage = () => {
    const nextLang: LanguageCode = currentLanguage === 'jp' ? 'en' : 'jp';
    changeLanguage(nextLang);
  };

  /**
   * Translation helper to select content based on current language.
   * 
   * @param translations - An object containing both Japanese and English versions of a string.
   * @returns The string corresponding to the current active language.
   */
  const t = (translations: { jp: string; en: string }): string => {
    return translations[currentLanguage];
  };

  return {
    /** The currently active language code ('jp' or 'en') */
    language: currentLanguage,
    /** Helper boolean to check if the current language is English */
    isEn: currentLanguage === 'en',
    /** Function to switch to a specific language */
    changeLanguage,
    /** Function to toggle the current language */
    toggleLanguage,
    /** Translation utility for localized content strings */
    t,
  };
};
